<template>
  <v-card>
    <v-card-title class="pb-1">
      <v-icon class="mr-1">mdi-ticket-confirmation-outline</v-icon>
      <v-text-field
        v-model="local_title"
        placeholder="Titel des Tickets"
        hide-details
        dense
        flat
        solo
        background-color="transparent"
        class="title-input"
        @blur="updateTitle"
        @keydown.enter="$event.target.blur()"
      />
      <v-spacer />
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-chip
            v-bind="attrs"
            v-on="on"
            small
            :color="state_config.color"
            :text-color="state_config.text || 'white'"
            class="ml-2"
          >
            <v-icon x-small left>{{ state_config.icon }}</v-icon>
            {{ state_config.name }}
            <v-icon x-small right>mdi-menu-down</v-icon>
          </v-chip>
        </template>
        <v-list dense>
          <v-list-item
            v-for="opt in state_options"
            :key="opt.value"
            :disabled="opt.value === ticket.state"
            @click="updateState(opt.value)"
          >
            <v-list-item-icon class="mr-2">
              <v-icon small :color="opt.color">{{ opt.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ opt.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-title>
    <v-card-subtitle class="pt-0 text-caption">
      <v-icon x-small class="mr-1">mdi-account-edit-outline</v-icon>
      {{ creator_name }}
      <span class="mx-2">·</span>
      <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
      Erstellt {{ formatDate(ticket.created_at) }}
      <span class="mx-2">·</span>
      Geändert {{ formatDate(ticket.updated_at) }}
    </v-card-subtitle>
    <v-card-text class="pt-2">
      <h5 class="text-caption grey--text mb-1">Configuration Items:</h5>
      <v-autocomplete
        :value="ticket.cis"
        :items="$store.state.ticketsystem.cis"
        item-text="name"
        item-value="id"
        placeholder="Keine"
        hide-details
        dense
        outlined
        multiple
        single-line
        chips
        small-chips
        deletable-chips
        @input="updateCis"
      />

      <h5 class="text-caption grey--text mb-1 mt-3 d-flex align-center">
        <v-icon x-small class="mr-1">mdi-eye-outline</v-icon>
        Sichtbar für:
        <span class="ml-2 font-weight-regular" style="text-transform: none;">
          (Ersteller und Workflow-Bearbeiter sehen das Ticket immer)
        </span>
      </h5>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-autocomplete
            :value="ticket.visible_groups || []"
            :items="availableGroups"
            prepend-inner-icon="mdi-account-group"
            placeholder="AD-Gruppen freigeben"
            hide-details
            dense
            outlined
            multiple
            single-line
            chips
            small-chips
            deletable-chips
            @input="updateVisibleGroups"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            :value="ticket.visible_users || []"
            :items="$store.state.ticketsystem.users"
            item-text="displayName"
            item-value="id"
            prepend-inner-icon="mdi-account"
            placeholder="Einzelne Benutzer freigeben"
            hide-details
            dense
            outlined
            multiple
            single-line
            chips
            small-chips
            deletable-chips
            @input="updateVisibleUsers"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
const STATE_OPTIONS = [
  { value: "OPEN",      name: "Offen",       icon: "mdi-progress-clock", color: "success" },
  { value: "DONE",      name: "Erledigt",    icon: "mdi-check",          color: "grey" },
  { value: "CANCELLED", name: "Abgebrochen", icon: "mdi-cancel",         color: "grey lighten-1" },
]

const STATE_CONFIG = {
  OPEN:      { name: "Offen",       icon: "mdi-progress-clock", color: "success" },
  DONE:      { name: "Erledigt",    icon: "mdi-check",          color: "grey" },
  CANCELLED: { name: "Abgebrochen", icon: "mdi-cancel",         color: "grey lighten-1", text: "grey darken-2" },
}

export default {
  name: "TicketHeader",
  props: ["ticket"],
  data() {
    return {
      local_title: this.ticket.title || "",
      state_options: STATE_OPTIONS,
    }
  },
  computed: {
    state_config() {
      return STATE_CONFIG[this.ticket.state] || { name: this.ticket.state || "—", color: "grey", icon: "mdi-help" }
    },
    creator_name() {
      const id = this.ticket.creator_id
      if (id == null) return "Unbekannt"
      const u = this.$store.state.ticketsystem.users.find(x => x.id === id)
      return u ? u.displayName : "Unbekannt"
    },
    availableGroups() {
      const set = new Set()
      for (const u of this.$store.state.ticketsystem.users) {
        if (Array.isArray(u.pms)) for (const p of u.pms) set.add(p)
      }
      return [...set].sort()
    },
  },
  watch: {
    "ticket.id"() {
      this.local_title = this.ticket.title || ""
    },
    "ticket.title"(v) {
      if (v !== this.local_title && document.activeElement && !document.activeElement.classList.contains("title-input")) {
        this.local_title = v || ""
      }
    },
  },
  methods: {
    updateTitle() {
      const trimmed = (this.local_title || "").trim()
      if (!trimmed || trimmed === this.ticket.title) return
      this.$store.dispatch("update", {
        payload_type: "PATCH",
        context: `/tickets/${this.ticket.id}`,
        payload: { title: trimmed },
      })
    },
    updateState(value) {
      if (value === this.ticket.state) return
      this.$store.dispatch("update", {
        payload_type: "PATCH",
        context: `/tickets/${this.ticket.id}`,
        payload: { state: value },
      })
    },
    updateCis(cis) {
      this.$store.dispatch("update", {
        payload_type: "PATCH",
        context: `/tickets/${this.ticket.id}`,
        payload: { cis },
      })
    },
    updateVisibleGroups(visible_groups) {
      this.$store.dispatch("update", {
        payload_type: "PATCH",
        context: `/tickets/${this.ticket.id}`,
        payload: { visible_groups },
      })
    },
    updateVisibleUsers(visible_users) {
      this.$store.dispatch("update", {
        payload_type: "PATCH",
        context: `/tickets/${this.ticket.id}`,
        payload: { visible_users },
      })
    },
    formatDate(s) {
      if (!s) return "—"
      return new Date(s).toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    },
  },
}
</script>

<style scoped>
.title-input >>> input {
  font-size: 1.25rem;
  font-weight: 500;
}
</style>
